"use client"

import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  CircularProgress,
  FormHelperText,List
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  Delete,
  AccountTree,
  Person,
  Email,
  Send,
  Search,
  FilterList,
  Visibility,
} from "@mui/icons-material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers"
import format from "date-fns/format"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

const ImprovementManagement = () => {
  const [pip, setPip] = useState([])
  const [employees, setEmployees] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", content: "", action: null })
  const [expandedPipId, setExpandedPipId] = useState(null)
  const [loading, setLoading] = useState({
    pip: false,
    employees: false,
    submit: false,
  })

  // State for filtering plans by employee
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState("")
  const [improvementAreaInput, setImprovementAreaInput] = useState({
    area: "",
    category: "Technical Skills",
    priority: "High"
  });
  
  const [improvementAreas, setImprovementAreas] = useState([]);
  const navigate = useNavigate()
  const location = useLocation()

  const [newPIP, setNewPIP] = useState({
    employee_id: "",
    employee_email: "",
    reason: "",
    objectives: "",
    startDate: null,
    endDate: null,
    milestones: "",
    meetingFrequency: "Weekly",
    status: "Active",
    progress: 0,
    improvementAreas: [] 
  })

  useEffect(() => {
    fetchInitialData()
  }, [])

  // Auto-populate email when employee is selected - same pattern as goals/reviews
  useEffect(() => {
    if (newPIP.employee_id) {
      const selectedEmployee = employees.find((emp) => emp.employee_id === newPIP.employee_id)
      if (selectedEmployee) {
        setNewPIP((prev) => ({
          ...prev,
          employee_email: selectedEmployee.email || "",
        }))
      }
    } else {
      setNewPIP((prev) => ({
        ...prev,
        employee_email: "",
      }))
    }
  }, [newPIP.employee_id, employees])

  const toggleExpandPIP = (pipId) => {
    setExpandedPipId((prev) => (prev === pipId ? null : pipId))
  }

  const fetchInitialData = async () => {
    try {
      await Promise.all([fetchEmployees(), fetchPlans()])
    } catch (error) {
      //console.error("Error fetching initial data:", error)
    }
  }

  const fetchEmployees = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, employees: true }))
      const response = await api.get(`/api/improvement-plans/employees?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      )

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid employee data received from server")
      }

      const employeeData = response.data

      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "Unknown",
        lastName: emp.lastName || emp.last_name || emp.lname || "",
        email: emp.email || "",
        department: emp.department || emp.dept || "General",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
      return normalizedEmployees
    } catch (error) {
      //console.error("Error fetching employees:", error)
      openSnackbar(`Failed to load employees: ${error.message}`, "error")
      setEmployees([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const fetchPlans = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, pip: true }))
      const response = await api.get(`/api/improvement-plans?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      )
      setPip(response.data || [])
      return response.data || []
    } catch (error) {
      //console.error("Error fetching plans:", error)
      openSnackbar(`Failed to load plans: ${error.message}`, "error")
      setPip([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, pip: false }))
    }
  }

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleConfirmDialogOpen = (title, content, action) => {
    setConfirmDialog({ open: true, title, content, action })
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ ...confirmDialog, open: false })
  }

  const executeConfirmAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action()
    }
    handleConfirmDialogClose()
  }

  const formatDateForAPI = (date) => {
    if (!date) return null
    return format(new Date(date), "yyyy-MM-dd")
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp.employee_id === employeeId)
    if (!employee) return "Unknown Employee"

    const lastName = employee.lastName && employee.lastName.trim() ? employee.lastName : ""
    return lastName ? `${employee.firstName} ${lastName}` : employee.firstName
  }

  const addPIP = async () => {
    if (
      !newPIP.employee_id ||
      !newPIP.reason ||
      !newPIP.objectives ||
      !newPIP.startDate ||
      !newPIP.endDate
    ) {
      openSnackbar("Please fill in all required fields", "error");
      return;
    }
  
    try {
      setLoading(prev => ({ ...prev, submit: true }));
  
      const payload = {
        ...newPIP,
        startDate: formatDateForAPI(newPIP.startDate),
        endDate: formatDateForAPI(newPIP.endDate),
      };
  
      // Get token
      const token = localStorage.getItem("token");
  
      let company_id = null;
  
      if (token) {
        try {
          const payloadDecoded = JSON.parse(atob(token.split(".")[1]));
          company_id = payloadDecoded.company_id;
        } catch (err) {
          //console.error("Failed to decode token:", err);
        }
      }
  
  
      const response = await api.post(
        "/api/improvement-plans",
        payload,
        {
          params: { company_id },
  
          headers: {
            Authorization: `Bearer ${token}`,
          },
  
          withCredentials: true,
        }
      );
  
  
      await fetchPlans();
  
      openSnackbar(
        "Improvement Plan created successfully and email notification sent!"
      );
  
      setNewPIP({
        employee_id: "",
        employee_email: "",
        reason: "",
        objectives: "",
        startDate: null,
        endDate: null,
        milestones: "",
        meetingFrequency: "Weekly",
        status: "Active",
        progress: 0,
      });
  
    } catch (error) {
      //console.error(
      //   "Error creating PIP:",
      //   error.response?.data || error.message
      // );
  
      openSnackbar(
        `Failed to create plan: ${
          error.response?.data?.error || error.message
        }`,
        "error"
      );
  
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };
  

  const updatePIPStatus = async (id, newStatus) => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading((prev) => ({ ...prev, pip: true }))

      const response = await api.put(`/api/improvement-plans/${id}`, { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )

      setPip(pip.map((plan) => (plan.id === id ? response.data : plan)))

      openSnackbar("PIP status updated successfully")
    } catch (error) {
      //console.error("Error updating PIP:", error)
      openSnackbar(`Failed to update status: ${error.message}`, "error")
    } finally {
      setLoading((prev) => ({ ...prev, pip: false }))
    }
  }

  const updatePIPProgress = async (id, newProgress) => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading((prev) => ({ ...prev, pip: true }))

      const response = await api.put(`/api/improvement-plans/${id}`, { progress: newProgress },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )

      setPip(pip.map((plan) => (plan.id === id ? response.data : plan)))

      openSnackbar("Progress updated successfully")
    } catch (error) {
      //console.error("Error updating progress:", error)
      openSnackbar(`Failed to update progress: ${error.message}`, "error")
    } finally {
      setLoading((prev) => ({ ...prev, pip: false }))
    }
  }

  const handleDeletePIP = (id) => {
    handleConfirmDialogOpen(
      "Delete Improvement Plan",
      "Are you sure you want to delete this plan? This action cannot be undone.",
      async () => {
        try {
            const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
          setLoading((prev) => ({ ...prev, pip: true }))

          await api.delete(`/api/improvement-plans/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )

          setPip(pip.filter((plan) => plan.id !== id))

          openSnackbar("Improvement plan deleted successfully")
        } catch (error) {
          //console.error("Error deleting PIP:", error)
        } finally {
          setLoading((prev) => ({ ...prev, pip: false }))
        }
      },
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "info"
      case "In Progress":
        return "warning"
      case "Completed":
        return "success"
      case "Extended":
        return "error"
      default:
        return "default"
    }
  }

  // Filter plans based on selected employee for viewing
  const filteredPlansForDisplay = useMemo(() => {
    if (!pip || pip.length === 0) return []

    if (!selectedEmployeeForView) return []

    return pip.filter((plan) => plan.employee_id === selectedEmployeeForView)
  }, [pip, selectedEmployeeForView])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3, boxShadow: 3, mx: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Performance Management
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={location.pathname}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiTab-root": { fontWeight: "medium", py: 2 },
              }}
            >
              <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
              <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
              <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
              <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
              <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
              <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
            </Tabs>
          </Box>
        </Paper>

        {location.pathname === "/improve" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Left side - Create Improvement Plan */}
              <Grid item xs={12} md={4}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", color: "#1976d2" }}
                    >
                      <Timeline sx={{ mr: 1 }} /> Create Improvement Plan
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Tooltip title="Select the employee for this improvement plan" arrow>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 0.5, fontSize: 16 }} />
                            Employee Name *
                          </Box>
                        </InputLabel>
                        <Select
                          value={newPIP.employee_id}
                          onChange={(e) => setNewPIP({ ...newPIP, employee_id: e.target.value })}
                          label="Employee Name"
                          disabled={loading.employees}
                        >
                          <MenuItem value="">
                            <em>{loading.employees ? "Loading employees..." : "Select Employee"}</em>
                          </MenuItem>
                          {employees.length > 0
                            ? employees.map((employee) => (
                                <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                  {getEmployeeName(employee.employee_id)}
                                 
                                </MenuItem>
                              ))
                            : !loading.employees && (
                                <MenuItem disabled>
                                  <em>No employees found</em>
                                </MenuItem>
                              )}
                        </Select>
                        {loading.employees && <CircularProgress sx={{ mt: 1 }} size={20} />}
                      </FormControl>
                    </Tooltip>

                    {/* Employee Email Field */}
                    <Tooltip title="Company email address (automatically fetched from database)" arrow>
                      <TextField
                        fullWidth
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 0.5, fontSize: 16 }} />
                           Email
                          </Box>
                        }
                        value={newPIP.employee_email}
                        margin="normal"
                        disabled
                        helperText="Email is automatically fetching based on employee name"
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />
                    </Tooltip>

                    <Tooltip
                      title="Specify the primary reason this improvement plan is being created"
                      placement="top"
                      arrow
                    >
                      <TextField
                        fullWidth
                        label="Reason for Plan"
                        value={newPIP.reason}
                        onChange={(e) => setNewPIP({ ...newPIP, reason: e.target.value })}
                        margin="normal"
                        required
                        variant="outlined"
                        placeholder="E.g., Performance improvement, Skill development"
                        sx={{ mb: 2 }}
                      />
                    </Tooltip>

                    <Tooltip
                      title="List specific, measurable objectives the employee needs to achieve"
                      placement="top"
                      arrow
                    >
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Objectives"
                        value={newPIP.objectives}
                        onChange={(e) => setNewPIP({ ...newPIP, objectives: e.target.value })}
                        margin="normal"
                        placeholder="Clear objectives that need to be achieved..."
                        required
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Tooltip>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="Start Date"
                          value={newPIP.startDate}
                          onChange={(date) => setNewPIP({ ...newPIP, startDate: date })}
                          renderInput={(params) => <TextField {...params} fullWidth required variant="outlined" />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="End Date"
                          value={newPIP.endDate}
                          onChange={(date) => setNewPIP({ ...newPIP, endDate: date })}
                          renderInput={(params) => <TextField {...params} fullWidth required variant="outlined" />}
                        />
                      </Grid>
                    </Grid>

                    <Tooltip
                      title="Define specific checkpoints to measure progress during the plan period"
                      placement="top"
                      arrow
                    >
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Key Milestones"
                        value={newPIP.milestones}
                        onChange={(e) => setNewPIP({ ...newPIP, milestones: e.target.value })}
                        margin="normal"
                        placeholder="Specific milestones to track progress..."
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Tooltip>

                    {/* Improvement Areas Section - NEW */}
<Box sx={{ mt: 2,mb:3, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
  <Typography variant="subtitle2" gutterBottom >
    Areas Requiring Improvement 
  </Typography>
  
  <Grid container spacing={2} sx={{ mb: 2 }}>
    <Grid item xs={12} sm={12}>
      <TextField
        fullWidth
        size="small"
        label="Improvement Area"
        value={improvementAreaInput.area}
        onChange={(e) => setImprovementAreaInput({
          ...improvementAreaInput,
          area: e.target.value
        })}
        placeholder="e.g., Java Programming, Communication Skills"
      />
    </Grid>
    
    <Grid item xs={12} sm={8}>
      <FormControl fullWidth size="small">
        <InputLabel>Category</InputLabel>
        <Select
          value={improvementAreaInput.category}
          onChange={(e) => setImprovementAreaInput({
            ...improvementAreaInput,
            category: e.target.value
          })}
          label="Category"
        >
          <MenuItem value="Technical Skills">Technical Skills</MenuItem>
          <MenuItem value="Soft Skills">Soft Skills</MenuItem>
          <MenuItem value="Management">Management</MenuItem>
          <MenuItem value="Domain Knowledge">Domain Knowledge</MenuItem>
          <MenuItem value="Communication">Communication</MenuItem>
        </Select>
      </FormControl>
    </Grid>
    
    <Grid item xs={12} sm={4}>
      <FormControl fullWidth size="small">
        <InputLabel>Priority</InputLabel>
        <Select
          value={improvementAreaInput.priority}
          onChange={(e) => setImprovementAreaInput({
            ...improvementAreaInput,
            priority: e.target.value
          })}
          label="Priority"
        >
          <MenuItem value="Critical">Critical</MenuItem>
          <MenuItem value="High">High</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Low">Low</MenuItem>
        </Select>
      </FormControl>
    </Grid>
    
    <Grid item xs={12} sm={4}>
      <Button
        fullWidth
        variant="outlined"
        size="small"
        onClick={() => {
          if (improvementAreaInput.area.trim()) {
            const newArea = {
              id: Date.now(),
              area: improvementAreaInput.area.trim(),
              category: improvementAreaInput.category,
              priority: improvementAreaInput.priority
            };
            setImprovementAreas([...improvementAreas, newArea]);
            setNewPIP({
              ...newPIP,
              improvementAreas: [...improvementAreas, newArea]
            });
            setImprovementAreaInput({
              area: "",
              category: "Technical Skills",
              priority: "High"
            });
          }
        }}
        sx={{ height: '40px',ml:12 }}
      >
        Add
      </Button>
    </Grid>
  </Grid>

  {/* Display Added Areas */}
  {improvementAreas.length > 0 && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        Added Improvement Areas:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {improvementAreas.map((area) => (
          <Chip
            key={area.id}
            label={`${area.area} (${area.priority})`}
            color={
              area.priority === 'Critical' ? 'error' :
              area.priority === 'High' ? 'warning' :
              area.priority === 'Medium' ? 'info' : 'default'
            }
            onDelete={() => {
              const updated = improvementAreas.filter(a => a.id !== area.id);
              setImprovementAreas(updated);
              setNewPIP({
                ...newPIP,
                improvementAreas: updated
              });
            }}
            size="small"
          />
        ))}
      </Box>
    </Box>
  )}
  
 
</Box>

                    <Tooltip title="Determine how often progress meetings should be scheduled" placement="top" arrow>
                      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                        <InputLabel>Meeting Frequency</InputLabel>
                        <Select
                          value={newPIP.meetingFrequency}
                          onChange={(e) => setNewPIP({ ...newPIP, meetingFrequency: e.target.value })}
                          label="Meeting Frequency"
                        >
                          <MenuItem value="Daily">Daily</MenuItem>
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                          <MenuItem value="Monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <Tooltip title="Create improvement plan and send email notification to employee" arrow>
                        <Button
                          variant="contained"
                          startIcon={loading.submit ? <CircularProgress size={20} /> : <Send />}
                          onClick={addPIP}
                          disabled={loading.submit}
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            fontWeight: "medium",
                          }}
                        >
                          {loading.submit ? "Creating..." : "Create Plan "}
                        </Button>
                      </Tooltip>
                      <FormHelperText sx={{ mt: 1, textAlign: "center", fontSize: "0.875rem" }}>
                        This will create the improvement plan and send an email notification to the employee
                      </FormHelperText>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right side - Employee Improvement Plans */}
              <Grid item xs={12} md={8}>
                <Paper elevation={3}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                      <Search sx={{ mr: 1 }} /> Employee Improvement Plans
                      {selectedEmployeeForView && ` (${filteredPlansForDisplay.length})`}
                    </Typography>
                    <Tooltip title="Select an employee to view their improvement plans" arrow>
                      <FormControl size="small" sx={{ minWidth: 280 }}>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FilterList sx={{ mr: 0.5, fontSize: 16 }} />
                            Select Employee to View Plans
                          </Box>
                        </InputLabel>
                        <Select
                          value={selectedEmployeeForView}
                          onChange={(e) => setSelectedEmployeeForView(e.target.value)}
                          label="Select Employee to View Plans"
                        >
                          <MenuItem value="">
                            <em>Choose an employee</em>
                          </MenuItem>
                          {employees.map((employee) => (
                            <MenuItem key={employee.employee_id} value={employee.employee_id}>
                              {getEmployeeName(employee.employee_id)}
                            
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Box>

                  <Divider />

                  {loading.pip ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading improvement plans...
                      </Typography>
                    </Box>
                  ) : !selectedEmployeeForView ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Search sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select an Employee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose an employee from the dropdown above to view their improvement plans.
                      </Typography>
                    </Box>
                  ) : filteredPlansForDisplay.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1">
                        No improvement plans found for {getEmployeeName(selectedEmployeeForView)}.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: "650px", overflow: "auto", p: 0 }}>
                    {filteredPlansForDisplay.map((plan) => (
  <React.Fragment key={plan.id}>
    <Paper elevation={2} sx={{ m: 2, p: 2, "&:hover": { bgcolor: "#f9f9f9" } }}>
      <ListItem button onClick={() => toggleExpandPIP(plan.id)}>
        <ListItemText
          primary={
            <Typography variant="subtitle1">
              {getEmployeeName(plan.employee_id)} (ID: {plan.employee_id})
            </Typography>
          }
          secondary={
            <>
              <Typography variant="caption" color="textSecondary">
                {format(new Date(plan.startDate), "MMM dd, yyyy")} -{" "}
                {format(new Date(plan.endDate), "MMM dd, yyyy")}
              </Typography>
              <Chip
                label={plan.status}
                size="small"
                color={getStatusColor(plan.status)}
                sx={{ ml: 70 }}
              />
              <Typography variant="body2" color="textSecondary">
                <strong>Reason:</strong> {plan.reason}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Meeting Frequency:</strong> {plan.meetingFrequency}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Objectives:</strong> {plan.objectives}
              </Typography>
              {plan.milestones && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Key Milestones:</strong> {plan.milestones}
                </Typography>
              )}
              
              {/* ✅ NEW: Display Improvement Areas */}
              {plan.improvementAreas && plan.improvementAreas.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Areas Requiring Improvement:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {plan.improvementAreas.map((area, idx) => (
                      <Chip
                        key={idx}
                        label={`${area.area} - ${area.category}`}
                        size="small"
                        color={
                          area.priority === 'Critical' ? 'error' :
                          area.priority === 'High' ? 'warning' :
                          area.priority === 'Medium' ? 'info' : 'default'
                        }
                        sx={{ fontWeight: 'medium' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          }
        />
      </ListItem>

      {expandedPipId === plan.id && (
        <Box sx={{ p: 2, bgcolor: "#f9f9f9" }}>
          <Grid container spacing={2}>
            {/* ✅ NEW: Detailed Improvement Areas Section */}
            {plan.improvementAreas && plan.improvementAreas.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'warning.50' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      📋 Improvement Areas Details
                    </Typography>
                    <List>
                      {plan.improvementAreas.map((area, idx) => (
                        <ListItem key={idx}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {area.area}
                              </Typography>
                              <Chip
                                label={area.priority}
                                size="small"
                                color={
                                  area.priority === 'Critical' ? 'error' :
                                  area.priority === 'High' ? 'warning' :
                                  area.priority === 'Medium' ? 'info' : 'default'
                                }
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Category: <strong>{area.category}</strong>
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Progress History
              </Typography>
              {plan.progressUpdates?.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                  {plan.progressUpdates.map((update, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: 4,
                        borderColor:
                          getStatusColor(update.status) === "error"
                            ? "#f44336"
                            : getStatusColor(update.status) === "warning"
                              ? "#ff9800"
                              : "#2196f3",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="caption" fontWeight="bold">
                          {format(new Date(update.date), "MMM dd, yyyy - hh:mm a")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={update.status}
                            size="small"
                            color={getStatusColor(update.status)}
                          />
                          <Chip
                            label={`${update.progress}%`}
                            size="small"
                            color={
                              update.progress === 100
                                ? "success"
                                : update.progress >= 50
                                  ? "primary"
                                  : "warning"
                            }
                          />
                        </Box>
                      </Box>
                      {update.taskCompletion && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Tasks Completed:</strong> {update.taskCompletion}
                        </Typography>
                      )}
                      {update.note && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {update.note}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography color="textSecondary">No progress updates recorded</Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  </React.Fragment>
))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onClose={handleConfirmDialogClose}>
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog.content}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose}>Cancel</Button>
            <Button onClick={executeConfirmAction} color="error" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  )
}

export default ImprovementManagement